import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { onIdTokenChanged, signOut } from "firebase/auth";
import {
  doc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig"; // Import db directly
import { EnvContext } from "./EnvContext";
import config from "../config";
import * as Sentry from "@sentry/react";

// Create the UserContext
export const UserContext = createContext(null);

export const UserContextProvider = ({ children }) => {
  //
  const environment = useContext(EnvContext);
  //
  const [authUser, setAuthUser] = useState(null);
  const [firestoreUserData, setFirestoreUserData] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true); // Added loading state
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);
  const [recentlyPurchasedProducts, setRecentlyPurchasedProducts] = useState(
    [],
  );

  // Memoize user object to prevent unnecessary re-renders
  const user = useMemo(() => {
    if (!authUser || !firestoreUserData) return null;
    return { uid: authUser.uid, ...firestoreUserData, ...authUser };
  }, [authUser, firestoreUserData]);

  // environment
  const collectionPath =
    environment === "staging"
      ? config.environment.staging.collectionPath
      : config.environment.production.collectionPath;

  useEffect(() => {
    if (user?.uid) {
      Sentry.setUser({
        id: user.uid,
        email: user.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => {
    const unsubscribeAuth = onIdTokenChanged(auth, (currentUser) => {
      setAuthUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!authUser) {
      setFirestoreUserData(null);
      setRecentlyViewedProducts([]);
      setRecentlyPurchasedProducts([]);
      return () => {};
    }

    const userRef = doc(db, `Users/UsersList/DataBase`, authUser.uid);

    // Get user data from Firestore
    const unsubscribeFirestore = onSnapshot(userRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        setFirestoreUserData(userData);

        // Fetch RecentlyViewedProducts
        const viewedRef = collection(
          db,
          `Users/UsersList/DataBase/${authUser.uid}/RecentlyViewedProducts`,
        );
        const viewedSnapshot = await getDocs(viewedRef);
        const viewedProducts = viewedSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRecentlyViewedProducts(viewedProducts);
        //
        // // Fetch RecentlyPurchasedProducts
        const purchasedRef = collection(
          db,
          `Users/UsersList/DataBase/${authUser.uid}/RecentlyPurchasedProducts`,
        );
        const purchasedSnapshot = await getDocs(purchasedRef);
        const purchasedProducts = purchasedSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRecentlyPurchasedProducts(purchasedProducts);
      } else {
        signOut(auth);
        console.error("User document does not exist!");
      }
      setLoading(false); // Set loading to false once user data is fetched
    });

    return () => unsubscribeFirestore();
  }, [authUser]);

  // cart items
  // Fetch cart data on user login
  useEffect(() => {
    let unsubscribeCart = null;

    const fetchCart = async () => {
      try {
        if (user) {
          // Query the Carts collection for the active cart
          const cartQuery = query(
            collection(db, `${collectionPath}Carts/CartsList/DataBase`),
            where("user_id", "==", user.uid),
            where("status", "==", "ordering"),
          );

          const cartSnapshot = await getDocs(cartQuery);

          if (!cartSnapshot.empty) {
            const cartDoc = cartSnapshot.docs[0]; // Assuming one active cart per user
            const cartId = cartDoc.id;

            // Set up a listener for real-time updates in the Items subcollection
            const itemsRef = collection(
              db,
              `${collectionPath}Carts/CartsList/DataBase/${cartId}/Items`,
            );

            unsubscribeCart = onSnapshot(itemsRef, (snapshot) => {
              const cartItems = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));

              setCart(cartItems); // Update the cart state dynamically
            });
          } else {
            setCart([]); // No active cart found
          }
        } else {
          setCart([]); // User not logged in
        }
      } catch (error) {
        console.error("Error fetching cart data:", error);
      }
    };

    fetchCart();

    return () => {
      if (unsubscribeCart) unsubscribeCart(); // Clean up the listener on unmount
    };
  }, [user, collectionPath]);

  return (
    <UserContext.Provider
      value={{
        user,
        cart,
        loading,
        recentlyViewedProducts,
        recentlyPurchasedProducts,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
